(() => {
  const createImage = (record, { viewer = false } = {}) => {
    const image = new Image();
    const source = viewer ? record.dataset.highres : record.dataset.src;
    image.src = source || record.dataset.src || '';
    if (!viewer) {
      image.srcset = record.dataset.srcset || '';
      image.sizes = record.dataset.sizes || '';
      image.loading = 'eager';
      image.fetchPriority = 'high';
      image.dataset.pdpMainImage = '';
    } else {
      image.loading = 'eager';
      image.dataset.pdpViewerImage = '';
    }
    image.decoding = 'async';
    image.alt = record.dataset.alt || '';
    if (record.dataset.width) image.width = Number(record.dataset.width);
    if (record.dataset.height) image.height = Number(record.dataset.height);
    return image;
  };

  const initializeGallery = (gallery) => {
    if (gallery.dataset.pdpGalleryReady === 'true') return;

    const thumbnails = Array.from(gallery.querySelectorAll('[data-pdp-media-thumb]'));
    const thumbnailRail = gallery.querySelector('[data-pdp-thumb-rail]');
    const thumbnailScroller = gallery.querySelector('[data-pdp-thumbs]');
    const thumbnailPrevious = gallery.querySelector('[data-pdp-thumb-previous]');
    const thumbnailNext = gallery.querySelector('[data-pdp-thumb-next]');
    const viewerThumbnails = Array.from(gallery.querySelectorAll('[data-pdp-viewer-thumb]'));
    const stage = gallery.querySelector('[data-pdp-main-stage]');
    const mainMediaFrame = stage?.closest('.pdp-gallery__media');
    const counter = gallery.querySelector('[data-pdp-media-counter]');
    const trigger = gallery.querySelector('[data-pdp-main-trigger]');
    const openButtons = Array.from(gallery.querySelectorAll('[data-pdp-open-viewer]'));
    const dialog = gallery.querySelector('[data-pdp-viewer]');
    const viewerStage = dialog?.querySelector('[data-pdp-viewer-stage]');
    const viewerMedia = dialog?.querySelector('[data-pdp-viewer-media]');
    const viewerCounter = dialog?.querySelector('[data-pdp-viewer-counter]');
    const close = dialog?.querySelector('[data-pdp-close-viewer]');
    const previous = dialog?.querySelector('[data-pdp-viewer-previous]');
    const next = dialog?.querySelector('[data-pdp-viewer-next]');
    const page = gallery.closest('[data-product-page]') || document;
    const selectedVariantControl = page.querySelector('[data-product-variant-control]');
    const selectedVariantMediaId = gallery.dataset.pdpGalleryFixedAspectRatio
      ? selectedVariantControl?.selectedOptions?.[0]?.dataset.mediaId
      : '';
    const selectedVariantMediaIndex = thumbnails.findIndex((thumb) => thumb.dataset.mediaId === selectedVariantMediaId);
    let activeIndex = selectedVariantMediaIndex >= 0
      ? selectedVariantMediaIndex
      : Math.max(0, thumbnails.findIndex((thumb) => thumb.classList.contains('is-active')));
    let returnFocus = null;

    const useMobileSwipeTrack = window.matchMedia('(max-width: 749px)').matches && thumbnails.length > 1;
    let mobileScrollFrame = null;

    if (!thumbnails.length || !stage) return;
    gallery.dataset.pdpGalleryReady = 'true';

    const setGalleryAspectRatio = (record) => {
      const fixedRatio = Number(gallery.dataset.pdpGalleryFixedAspectRatio);
      const rawRatio = Number.isFinite(fixedRatio) && fixedRatio > 0
        ? fixedRatio
        : Number(record?.dataset.mediaAspectRatio || gallery.dataset.pdpGalleryAspectRatio || 1);
      const ratio = Number.isFinite(rawRatio) ? Math.min(Math.max(rawRatio, 0.8), 1.333) : 1;
      gallery.style.setProperty('--pdp-gallery-aspect-ratio', String(ratio));
      gallery.dataset.pdpGalleryAspectRatio = String(ratio);
    };

    const isDesktopRail = () => window.matchMedia('(min-width: 1100px)').matches;
    const syncThumbnailRailHeight = () => {
      if (!thumbnailRail || !mainMediaFrame) return;
      if (!isDesktopRail()) {
        gallery.style.removeProperty('--pdp-main-media-height');
        return;
      }
      const height = mainMediaFrame.getBoundingClientRect().height;
      if (height > 0) gallery.style.setProperty('--pdp-main-media-height', `${height}px`);
    };
    const revealThumbnail = (thumb, behavior = 'smooth') => {
      if (!thumbnailScroller || !thumb || !isDesktopRail()) return;
      const thumbTop = thumb.offsetTop;
      const thumbBottom = thumbTop + thumb.offsetHeight;
      const viewportTop = thumbnailScroller.scrollTop;
      const viewportBottom = viewportTop + thumbnailScroller.clientHeight;
      if (thumbTop < viewportTop) thumbnailScroller.scrollTo({ top: thumbTop, behavior });
      if (thumbBottom > viewportBottom) thumbnailScroller.scrollTo({ top: thumbBottom - thumbnailScroller.clientHeight, behavior });
    };
    const updateThumbnailRail = () => {
      if (!thumbnailRail || !thumbnailScroller) return;
      const overflow = isDesktopRail() && thumbnailScroller.scrollHeight > thumbnailScroller.clientHeight + 1;
      syncThumbnailRailHeight();
      const atStart = thumbnailScroller.scrollTop <= 1;
      const atEnd = thumbnailScroller.scrollTop + thumbnailScroller.clientHeight >= thumbnailScroller.scrollHeight - 1;
      thumbnailRail.toggleAttribute('data-pdp-thumb-overflow', overflow);
      if (thumbnailPrevious) {
        thumbnailPrevious.hidden = !overflow;
        thumbnailPrevious.disabled = !overflow || atStart;
      }
      if (thumbnailNext) {
        thumbnailNext.hidden = !overflow;
        thumbnailNext.disabled = !overflow || atEnd;
      }
    };
    const stepThumbnailRail = (direction) => {
      if (!thumbnailScroller) return;
      const step = Math.max(thumbnails[0]?.offsetHeight || 0, thumbnailScroller.clientHeight * .72);
      thumbnailScroller.scrollBy({ top: direction * step, behavior: 'smooth' });
    };
    thumbnailPrevious?.addEventListener('click', () => stepThumbnailRail(-1));
    thumbnailNext?.addEventListener('click', () => stepThumbnailRail(1));
    thumbnailScroller?.addEventListener('scroll', updateThumbnailRail, { passive: true });
    const railResizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(updateThumbnailRail)
      : null;
    if (mainMediaFrame) railResizeObserver?.observe(mainMediaFrame);
    window.addEventListener('resize', updateThumbnailRail, { passive: true });

    const templateFor = (record, viewer = false) => {
      const mode = viewer ? 'viewer' : 'main';
      return Array.from(gallery.querySelectorAll(`template[data-pdp-media-template][data-pdp-template-mode="${mode}"]`))
        .find((template) => template.dataset.mediaId === record.dataset.mediaId);
    };

    const buildMobileSwipeTrack = () => {
      if (!useMobileSwipeTrack) return;
      const slides = thumbnails.map((record, index) => {
        const slide = document.createElement('div');
        slide.className = 'pdp-gallery__mobile-slide';
        slide.dataset.pdpMobileMediaIndex = String(index);
        const content = record.dataset.mediaType === 'image'
          ? createImage(record)
          : templateFor(record)?.content.cloneNode(true);
        if (content) slide.append(content);
        return slide;
      });
      stage.replaceChildren(...slides);
      stage.dataset.mobileSwipeTrack = '';
      const initialSlide = stage.querySelector(`[data-pdp-mobile-media-index="${activeIndex}"]`);
      if (initialSlide) stage.scrollLeft = initialSlide.offsetLeft;
      stage.addEventListener('scroll', () => {
        if (mobileScrollFrame) return;
        mobileScrollFrame = window.requestAnimationFrame(() => {
          mobileScrollFrame = null;
          const width = stage.clientWidth;
          const nextIndex = width ? Math.round(stage.scrollLeft / width) : 0;
          if (nextIndex >= 0 && nextIndex < thumbnails.length && nextIndex !== activeIndex) {
            activeIndex = nextIndex;
            setActiveControls(nextIndex);
          }
        });
      }, { passive: true });
    };
    const setActiveControls = (index) => {
      thumbnails.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.toggleAttribute('aria-current', active);
      });
      viewerThumbnails.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.toggleAttribute('aria-current', active);
      });
      const record = thumbnails[index];
      setGalleryAspectRatio(record);
      revealThumbnail(record);
      if (counter) counter.textContent = `${index + 1} / ${thumbnails.length}`;
      if (viewerCounter) viewerCounter.textContent = `${index + 1} / ${thumbnails.length}`;
      updateThumbnailRail();
      if (trigger) trigger.hidden = record?.dataset.mediaType !== 'image';
    };
    const renderMain = (index, { scroll = false } = {}) => {
      const record = thumbnails[index];
      if (!record) return;
      activeIndex = index;
      setActiveControls(index);
      if (useMobileSwipeTrack) {
        const slide = stage.querySelector(`[data-pdp-mobile-media-index="${index}"]`);
        if (scroll && slide) stage.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        return;
      }
      const nextContent = record.dataset.mediaType === 'image'
        ? createImage(record)
        : templateFor(record)?.content.cloneNode(true);
      if (!nextContent) return;
      stage.replaceChildren(nextContent);
      if (scroll) record.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    };
      const mainMedia = stage.querySelector('img, video, model-viewer, iframe');
      mainMedia?.addEventListener('load', updateThumbnailRail, { once: true });
      window.requestAnimationFrame(updateThumbnailRail);
    const renderViewer = (index) => {
      const record = thumbnails[index];
      if (!record || !viewerMedia) return;
      const nextContent = record.dataset.mediaType === 'image'
        ? createImage(record, { viewer: true })
        : templateFor(record, true)?.content.cloneNode(true);
      if (!nextContent) return;
      viewerMedia.replaceChildren(nextContent);
      viewerStage?.classList.remove('is-zoomed');
      setActiveControls(index);
      const image = viewerMedia.querySelector('[data-pdp-viewer-image]');
      image?.addEventListener('click', () => viewerStage?.classList.toggle('is-zoomed'));
    };

    const setMedia = (index, options = {}) => {
      const normalized = (index + thumbnails.length) % thumbnails.length;
      renderMain(normalized, options);
      if (dialog?.open) renderViewer(normalized);
    };

    const openViewer = (event) => {
      if (!dialog || dialog.open) return;
      returnFocus = event?.currentTarget || trigger || thumbnails[activeIndex];
      renderViewer(activeIndex);
      dialog.showModal();
      close?.focus();
    };

    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => setMedia(index, { scroll: true }));
      thumb.addEventListener('keydown', (event) => {
        let nextIndex = null;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = index + 1;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = index - 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = thumbnails.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        const normalized = (nextIndex + thumbnails.length) % thumbnails.length;
        setMedia(normalized, { scroll: true });
        thumbnails[normalized].focus();
      });
    });

    viewerThumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => setMedia(index, { scroll: true }));
    });
    openButtons.forEach((button) => button.addEventListener('click', openViewer));
    trigger?.addEventListener('click', openViewer);
    close?.addEventListener('click', () => dialog?.close());
    previous?.addEventListener('click', () => setMedia(activeIndex - 1, { scroll: true }));
    next?.addEventListener('click', () => setMedia(activeIndex + 1, { scroll: true }));

    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setMedia(activeIndex - 1, { scroll: true });
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setMedia(activeIndex + 1, { scroll: true });
      }
    });
    dialog?.addEventListener('close', () => {
      viewerStage?.classList.remove('is-zoomed');
      returnFocus?.focus();
    });

    page.addEventListener('change', (event) => {
      const control = event.target.closest?.('[data-product-variant-control]');
      if (!control) return;
      const mediaId = control.selectedOptions?.[0]?.dataset.mediaId;
      const nextIndex = thumbnails.findIndex((thumb) => thumb.dataset.mediaId === mediaId);
      if (nextIndex >= 0) setMedia(nextIndex, { scroll: true });
    });

    buildMobileSwipeTrack();
    updateThumbnailRail();
    if (useMobileSwipeTrack) {
      let pointerStart = null;
      stage.addEventListener('pointerdown', (event) => {
        pointerStart = { x: event.clientX, y: event.clientY };
      }, { passive: true });
      stage.addEventListener('pointerup', (event) => {
        if (!pointerStart) return;
        const isTap = Math.abs(event.clientX - pointerStart.x) < 8 && Math.abs(event.clientY - pointerStart.y) < 8;
        pointerStart = null;
        if (isTap) openViewer({ currentTarget: openButtons[0] || thumbnails[activeIndex] });
      }, { passive: true });
    }
    renderMain(activeIndex);
  };

  const initialize = (root = document) => {
    const galleries = root.matches?.('[data-pdp-gallery]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-pdp-gallery]') || []);
    galleries.forEach(initializeGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
})();
